import FetchCostIndicators from '@Application/Indicators/Queries/FetchCostIndicators';
import buildIndicatorQueryKey from '@Application/Indicators/buildIndicatorQueryKey';
import fetchCostIndicators from '@Application/Indicators/fetchCostIndicators';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import useRefetchInterval, {
	SUPERVISION_REFETCH_INTERVAL_IN_MILLISECONDS,
} from '@UI/Hooks/useRefetchInterval';
import ComparisonLine from '@UI/Pages/Home/Shared/ComparisonLine';
import IndicatorBlockState from '@UI/Pages/Home/Shared/IndicatorBlockState';
import IndicatorCard from '@UI/Pages/Home/Shared/IndicatorCard';
import formatEuros from '@UI/Helpers/formatEuros';
import formatIndicatorPeriod from '@UI/Pages/Home/Helpers/formatIndicatorPeriod';
import useHomeIndicatorScope from '@UI/Pages/Home/Hooks/useHomeIndicatorScope';
import useRetainedIndicatorView from '@UI/Pages/Home/Hooks/useRetainedIndicatorView';
import useWidenIndicatorAction from '@UI/Pages/Home/Hooks/useWidenIndicatorAction';
import homePeriodSignal from '@UI/Pages/Home/Signals/homePeriodSignal';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import type * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

const figureStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
`;

const figureRowStyle = css`
	margin: 0;
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 0.625rem;
`;

const bigNumberStyle = css`
	font-family: ${theme.fonts.display};
	font-size: 3rem;
	font-weight: ${theme.fontWeights.semibold};
	line-height: 1;
	letter-spacing: -0.04em;
	color: ${theme.tints.amber.ink};

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		font-size: 2.5rem;
	}
`;

const unpricedNoteStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	line-height: 1.5;
	color: ${theme.colors.inkSecondary};
`;

/** Estimated cost of the AI calls of the scope; mounted only under `AI_USAGE_READ`. */
const CostsBlock: React.FC = () => {
	const dispatch = useDispatch();
	const period = useSignalValue(homePeriodSignal);
	const { status, indicatorScope } = useHomeIndicatorScope();
	const widenAction = useWidenIndicatorAction(false);

	const queryKey = indicatorScope === null ? null : buildIndicatorQueryKey(indicatorScope, period);

	const { data, error, refetch } = useStoreQuery({
		selector: (state) => state.indicatorsStore.costs,
		stopOn: (view) => queryKey === null || (view !== null && view.queryKey === queryKey),
		query: () => {
			if (indicatorScope === null) {
				return Promise.resolve();
			}

			return dispatch(fetchCostIndicators(new FetchCostIndicators({ scope: indicatorScope, period })));
		},
		deps: [queryKey],
	});

	useRefetchInterval(refetch, SUPERVISION_REFETCH_INTERVAL_IN_MILLISECONDS);

	const { view, isRefreshing } = useRetainedIndicatorView(data, queryKey);

	const renderBody = (): React.ReactNode => {
		if (status === 'unavailable') {
			return <IndicatorBlockState kind="empty" title="Aucune agence ne vous est rattachée." />;
		}

		if (error !== null) {
			return <IndicatorBlockState kind="error" onRetry={refetch} />;
		}

		if (view === null) {
			return <IndicatorBlockState kind="loading" variant="costs" />;
		}

		if (view.isEmpty) {
			const formattedPeriod = formatIndicatorPeriod(view.period);

			return (
				<IndicatorBlockState
					kind="empty"
					title="Aucun coût sur la période"
					range={`${formattedPeriod.from} – ${formattedPeriod.to} (heure de Paris)`}
					{...(widenAction === null
						? {}
						: { actionLabel: widenAction.label, onAction: widenAction.run })}
				/>
			);
		}

		if (view.isEntirelyUnpriced) {
			return <IndicatorBlockState kind="empty" title="Tarif non renseigné" />;
		}

		return (
			<div css={figureStyle}>
				<p css={figureRowStyle}>
					<span css={bigNumberStyle}>{formatEuros(view.total.current)}</span>
				</p>
				<ComparisonLine
					comparison={view.total}
					days={view.period.days}
					subject="coût"
					formatValue={formatEuros}
				/>
				<ConditionalLoader data={view.unpricedCallCount !== null && view.unpricedCallCount > 0}>
					<p css={unpricedNoteStyle}>
						{view.unpricedCallCount === 1
							? 'dont 1 appel sans tarif'
							: `dont ${view.unpricedCallCount} appels sans tarif`}
					</p>
				</ConditionalLoader>
				<ConditionalLoader data={view.unpricedCallCount === null && view.hasUnpricedCalls}>
					<p css={unpricedNoteStyle}>dont des traitements sans tarif, non comptés</p>
				</ConditionalLoader>
			</div>
		);
	};

	return (
		<IndicatorCard
			title="Coût IA estimé"
			tooltip="Coût estimé des appels d'IA de la période, calculé avec les tarifs en vigueur au moment de chaque appel. Distinct de la facture du fournisseur."
			band={theme.home.indicatorBand.costs}
			isRefreshing={isRefreshing && error === null}
		>
			{renderBody()}
		</IndicatorCard>
	);
};

export default CostsBlock;
